import {
  PRODUCT_PHOTO_HEIGHT,
  PRODUCT_PHOTO_WIDTH
} from 'components/structure-page/2d/product-photo';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  CondenserPhotoData,
  GridObjectType,
  GridSpaceDataType,
  ZoneOutlineData
} from 'data/types';
import { isWallMountedAirHandler } from 'utils/air-handler-mounting';
import { SpaceLabelObstacle } from 'utils/space-label-position';
import { getZonePhotoCenter } from 'utils/zone-outline';

const AIR_HANDLER_SIZE = 3 * STRUCTURE_GRID_SIZE;

function pointInSpace(x: number, y: number, space: GridSpaceDataType): boolean {
  const { position } = space;
  return (
    x >= position.x &&
    x <= position.x + position.width &&
    y >= position.y &&
    y <= position.y + position.height
  );
}

function addObstacle(
  map: Map<string, SpaceLabelObstacle[]>,
  spaceId: string,
  obstacle: SpaceLabelObstacle
) {
  const existing = map.get(spaceId);
  if (existing) {
    existing.push(obstacle);
  } else {
    map.set(spaceId, [obstacle]);
  }
}

function addPhotoObstacle(
  map: Map<string, SpaceLabelObstacle[]>,
  gridSpaces: GridSpaceDataType[],
  centerX: number,
  centerY: number
) {
  gridSpaces.forEach((space) => {
    if (!pointInSpace(centerX, centerY, space)) return;
    addObstacle(map, space.spaceId, {
      x: centerX - PRODUCT_PHOTO_WIDTH / 2 - space.position.x,
      y: centerY - PRODUCT_PHOTO_HEIGHT / 2 - space.position.y,
      width: PRODUCT_PHOTO_WIDTH,
      height: PRODUCT_PHOTO_HEIGHT
    });
  });
}

export function collectSpaceLabelObstacles(
  gridSpaces: GridSpaceDataType[],
  objects: GridObjectType[],
  zoneOutlines: ZoneOutlineData[],
  condenserPhotos: CondenserPhotoData[] = []
): Map<string, SpaceLabelObstacle[]> {
  const map = new Map<string, SpaceLabelObstacle[]>();

  objects.forEach((obj) => {
    if (
      obj.type !== 'air-handler' ||
      !obj.airHandlerTypeCode ||
      isWallMountedAirHandler(obj.airHandlerTypeCode)
    ) {
      return;
    }

    const ahLeft = obj.position.x * STRUCTURE_GRID_SIZE;
    const ahTop = obj.position.y * STRUCTURE_GRID_SIZE;
    const ahCenterX = ahLeft + AIR_HANDLER_SIZE / 2;
    const ahCenterY = ahTop + AIR_HANDLER_SIZE / 2;

    gridSpaces.forEach((space) => {
      if (!pointInSpace(ahCenterX, ahCenterY, space)) return;
      addObstacle(map, space.spaceId, {
        x: ahLeft - space.position.x,
        y: ahTop - space.position.y,
        width: AIR_HANDLER_SIZE,
        height: AIR_HANDLER_SIZE
      });
    });
  });

  zoneOutlines.forEach((zone) => {
    const center = getZonePhotoCenter(zone, gridSpaces);
    if (!center) return;
    addPhotoObstacle(map, gridSpaces, center.x, center.y);
  });

  condenserPhotos.forEach((photo) => {
    addPhotoObstacle(map, gridSpaces, photo.position.x, photo.position.y);
  });

  return map;
}
